import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { CaretDown } from '@phosphor-icons/react/dist/ssr/CaretDown';
import { Check } from '@phosphor-icons/react/dist/ssr/Check';
import { ChatCircle } from '@phosphor-icons/react/dist/ssr/ChatCircle';
import { DownloadSimple } from '@phosphor-icons/react/dist/ssr/DownloadSimple';
import { IconCircleButton } from '../../../UISystem/Icons/IconCircleButton';
import ConnectionAvatar from '../../../UISystem/ConnectionAvatar';
import RetryImage from '../../../UI/RetryImage';
import { ButtonRounded } from '../../../UISystem/Buttons';
import { COLORS } from '../../../../styles/UISystem';
import {
  ALL_CONTACT_TYPES,
  RELEVANCE_ORDER,
  CONTACT_FIELDS,
  openContactLink,
} from '../../../../utils/contactTypeConfig';
import { formatMomentBanner } from '../../../../utils/locationFormatters';
import { formatPhoneNumber } from '../../../../utils/formatters';
import { firstNameOf } from '../../../../utils/contactUtils';
import { latestSharedImageUrl } from '../../../../utils/chatHelpers';

// Channel order mirrors ContactIconRow: phone leads, then relevance, then the
// rest; custom is excluded. (A second copy of ContactIconRow's ordering — the
// contactKeys consolidation is logged in Future Work.)
const ORDERED_CHANNEL_TYPES = [
  ...new Set(['phone', ...RELEVANCE_ORDER, ...CONTACT_FIELDS.filter((t) => t !== 'custom')]),
];

// Staggered arrival under the star burst — module-scope so the element type
// is stable across renders (/simplify: an inline component remounted the
// subtree — and refetched the moment-photo — on every live message tick)
const Appear = ({ celebrate, delay, children }) =>
  celebrate ? (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35, ease: 'easeOut' }}
      style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
    >
      {children}
    </motion.div>
  ) : (
    children
  );

/**
 * AnonTextCard — the TEXT variant's card page (variants block plan 2, epic
 * §3/§8; layout settled across device pass 1k).
 *
 * ONE layout, both stages: host photo + name as hero, small quiet channel
 * pills stacked under the name (business-card rows read transactional),
 * then the moment banner — the friendly "We met at ..." sentence as plain
 * centered text (no box) — which only exists once the host shared the place
 * (formatMomentBanner returns '' while the venue is unconfirmed — never
 * neighborhood-first with a silent upgrade). No About row ever pre-share
 * (anti-teaser rule).
 *
 * POST-SHARE (user mock, device pass 1k — the third iteration of this
 * state: full takeover → info kept → THIS): the clean DONE screen. One
 * underlined door up top — "{Name}'s info in Royale app" — carries both
 * the payload and the app in a single named link (info never yanked, one
 * tap away); the grey card confirms "✓ You and {Name} are connected" with
 * the moment-photo inside when one exists; [Get link] (the self-text
 * bookmark, retires on tap) + [Save info] stay pinned low. The parent's
 * star burst answers the Connect tap over this state arriving staggered.
 */
const AnonTextCard = ({
  profileData,
  connectionMetadata,
  chatMessages,
  shared,
  celebrate = false,
  textLinkSent,
  phoneSaved = false,
  onShareNumber,
  onShareOptions,
  onSaveToPhone,
  onTextLink,
  onBack,
}) => {
  // The host's moment-photo — the one irreplaceable artifact (user ruling,
  // device pass 1f: "if Profile takes a photo, it's meant to be shared").
  // Existing rail: same helper the People thumbnails use; arrives LIVE via
  // the chat message stream if the host snaps it while this view is up.
  const momentPhotoUrl = latestSharedImageUrl(chatMessages);
  const firstName = firstNameOf(profileData?.name);
  const banner = formatMomentBanner(connectionMetadata);
  const sharedFields = profileData?.sharedFields || [];

  // Live channel pills — value present AND shared (value alone is never
  // proof of consent; same predicate discipline as every display surface)
  const channels = ORDERED_CHANNEL_TYPES.map((type) => ({
    type,
    config: ALL_CONTACT_TYPES[type],
    value: profileData?.[type],
  })).filter(({ type, config, value }) => value && config?.getLink && sharedFields.includes(type));

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        backgroundColor: COLORS.WHITE,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* POST-SHARE: the clean done screen (user mock) — the info folds
          into the one named door up top, the confirmation gets the stage */}
      {shared ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '0 24px',
            paddingTop: 'calc(env(safe-area-inset-top) + 28px)',
            paddingBottom: 'calc(env(safe-area-inset-bottom) + 150px)',
          }}
        >
          <div
            onClick={onBack}
            style={{
              fontSize: '15px',
              color: '#000000',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {firstName ? `${firstName}'s info in Royale app` : 'Info in Royale app'}
          </div>

          <div
            style={{
              flex: 1,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              paddingBottom: '15vh',
            }}
          >
            <Appear celebrate={celebrate} delay={0.3}>
              <div
                style={{
                  width: '100%',
                  maxWidth: '320px',
                  backgroundColor: '#F9F9FB',
                  borderRadius: '20px',
                  padding: '36px 24px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '14px',
                  textAlign: 'center',
                }}
              >
                <Check size={26} color="#000000" />
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#000000' }}>
                  {firstName ? `You and ${firstName} are connected` : "You're connected"}
                </div>
                {/* The moment-photo, INSIDE the card when one exists
                    (anchoring ruling, device pass 1f: it is evidence of the
                    sentence above it) */}
                {momentPhotoUrl && (
                  <RetryImage
                    src={momentPhotoUrl}
                    alt=""
                    style={{
                      maxWidth: '100%',
                      maxHeight: '110px',
                      borderRadius: '12px',
                      objectFit: 'cover',
                    }}
                  />
                )}
              </div>
            </Appear>
          </div>
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '0 24px',
            paddingTop: 'calc(env(safe-area-inset-top) + 14px)',
            paddingBottom: 'calc(env(safe-area-inset-bottom) + 150px)',
          }}
        >
          <ConnectionAvatar photoUrl={profileData?.photoUrl} name={profileData?.name} size={72} />
          <div
            style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#000000',
              textAlign: 'center',
              marginTop: '10px',
            }}
          >
            {profileData?.name || ''}
          </div>

          {/* Channel pills — MUCH smaller, stacked under the name (user
            ruling, device pass 1k: full-width rows read business-card
            transactional). Quiet grey, still real taps. */}
          {channels.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '8px',
                marginTop: '10px',
                maxWidth: '300px',
              }}
            >
              {channels.map(({ type, config, value }) => {
                const Icon = config.Icon;
                return (
                  <div
                    key={type}
                    onClick={() => {
                      const link = config.getLink(value);
                      if (link) openContactLink(link);
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E3E3E7',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    {Icon && <Icon size={14} color="#8E8E93" style={{ flexShrink: 0 }} />}
                    <span
                      style={{
                        fontSize: '13px',
                        color: '#8E8E93',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {config.category === 'phone-like' ? formatPhoneNumber(value) : value}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* The moment banner — the friendly sentence as plain centered
            text, no box (device pass 1k); only once the host shared the
            place */}
          {banner && (
            <div
              style={{
                fontSize: '16px',
                color: '#000000',
                textAlign: 'center',
                lineHeight: 1.45,
                marginTop: '14px',
                padding: '8px 0',
                maxWidth: '320px',
              }}
            >
              {banner}
            </div>
          )}
        </div>
      )}

      {/* Fixed action overlay — always visible; the card scrolls beneath */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          padding: '16px 24px calc(20px + env(safe-area-inset-bottom))',
          background: 'linear-gradient(to top, rgba(255,255,255,1) 75%, rgba(255,255,255,0))',
        }}
      >
        {shared ? (
          // "Get link" (device pass 1k — "send" read as sending somewhere;
          // the action is receiving the bookmark). Retires on tap.
          !textLinkSent && (
            <Appear celebrate={celebrate} delay={0.6}>
              <ButtonRounded
                text={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    <ChatCircle size={18} />
                    Get link
                  </span>
                }
                onClick={onTextLink}
                variant="primary"
                size="auto"
                style={{ minWidth: '190px' }}
              />
            </Appear>
          )
        ) : (
          // [Share number] = the phone fast path; the caret (mock, device
          // pass 1j) opens the channel selection. Caret hangs right of the
          // centered button so the button column stays aligned.
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
            <ButtonRounded
              text="Share number"
              onClick={onShareNumber}
              variant="primary"
              size="auto"
              style={{ minWidth: '190px' }}
            />
            <div
              style={{
                position: 'absolute',
                left: 'calc(100% + 10px)',
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              <IconCircleButton
                icon={CaretDown}
                size={18}
                buttonSize={40}
                onClick={onShareOptions}
              />
            </div>
          </div>
        )}
        {/* Retires on tap (device pass 1i ruling — declutter; supersedes
            the epic's persists-at-every-stage) */}
        {!phoneSaved && (
          <ButtonRounded
            text={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <DownloadSimple size={18} />
                Save info
              </span>
            }
            onClick={onSaveToPhone}
            variant="secondary"
            size="auto"
            style={{ minWidth: '190px' }}
          />
        )}
      </div>
    </div>
  );
};

AnonTextCard.propTypes = {
  profileData: PropTypes.object,
  connectionMetadata: PropTypes.object,
  chatMessages: PropTypes.array,
  shared: PropTypes.bool.isRequired,
  celebrate: PropTypes.bool,
  textLinkSent: PropTypes.bool.isRequired,
  phoneSaved: PropTypes.bool,
  onShareNumber: PropTypes.func.isRequired,
  onShareOptions: PropTypes.func.isRequired,
  onSaveToPhone: PropTypes.func.isRequired,
  onTextLink: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default AnonTextCard;
